import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../../api/client';
import { useToast } from '../../../hooks/useToast';
import { FiPlus, FiDownload, FiShield } from 'react-icons/fi';

const Compliance = () => {
  const { success, error: showError } = useToast();
  const [policies, setPolicies] = useState([]);
  const [gdprRequests, setGdprRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddPolicy, setShowAddPolicy] = useState(false);
  const [policyForm, setPolicyForm] = useState({ entityType: '', retentionPeriodMonths: 84, basis: '' });

  const [showAddRequest, setShowAddRequest] = useState(false);
  const [requestForm, setRequestForm] = useState({ subjectType: 'employee', subjectId: '', requestType: 'export', legalBasis: '' });

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/compliance/retention/policies'),
      apiClient.get('/compliance/gdpr/requests'),
    ]).then(([policiesRes, requestsRes]) => {
      setPolicies(policiesRes.data.data);
      setGdprRequests(requestsRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load compliance data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddPolicy = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/compliance/retention/policies', policyForm);
      success('✅ Retention policy added');
      setShowAddPolicy(false);
      setPolicyForm({ entityType: '', retentionPeriodMonths: 84, basis: '' });
      loadAll();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not add policy'));
    }
  };

  const handleAddRequest = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/compliance/gdpr/requests', requestForm);
      success('✅ GDPR request logged');
      setShowAddRequest(false);
      setRequestForm({ subjectType: 'employee', subjectId: '', requestType: 'export', legalBasis: '' });
      loadAll();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not create request'));
    }
  };

  const handleProcessRequest = async (requestId, status) => {
    try {
      await apiClient.patch(`/compliance/gdpr/requests/${requestId}/process`, { status });
      success(`✅ Request marked ${status}`);
      loadAll();
    } catch (err) {
      showError(getErrorMessage(err, 'Could not update request'));
    }
  };

  const handleExportMyData = async () => {
    try {
      const { data } = await apiClient.get('/compliance/gdpr/export/me');
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-data-export.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showError(getErrorMessage(err, 'Could not export data'));
    }
  };

  if (loading) return <div className="dashboard-page"><p>Loading...</p></div>;

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Data Retention & Compliance</h1>
        <p>Retention policies and GDPR data subject requests</p>
      </div>

      <section className="page-section">
        <div className="section-header">
          <h2>Your Own Data</h2>
        </div>
        <p style={{ color: '#6b7280', marginBottom: '1rem' }}>Download a copy of your own personal data on file, right now — no request/approval needed.</p>
        <button className="btn btn-outline" style={{ width: 'auto' }} onClick={handleExportMyData}>
          <FiDownload size={16} /> Export My Data
        </button>
      </section>

      <section className="page-section">
        <div className="section-header">
          <h2><FiShield size={18} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />Retention Policies</h2>
          <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowAddPolicy((s) => !s)}>
            <FiPlus size={14} /> {showAddPolicy ? 'Cancel' : 'Add Policy'}
          </button>
        </div>

        {showAddPolicy && (
          <form onSubmit={handleAddPolicy} className="form-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="form-field">
              <label>Entity Type *</label>
              <input required value={policyForm.entityType} onChange={(e) => setPolicyForm(p => ({ ...p, entityType: e.target.value }))} placeholder="e.g. employee, invoice" />
            </div>
            <div className="form-field">
              <label>Retention Period (months) *</label>
              <input type="number" min="1" required value={policyForm.retentionPeriodMonths} onChange={(e) => setPolicyForm(p => ({ ...p, retentionPeriodMonths: Number(e.target.value) }))} />
            </div>
            <div className="form-field full-width">
              <label>Basis</label>
              <input value={policyForm.basis} onChange={(e) => setPolicyForm(p => ({ ...p, basis: e.target.value }))} placeholder="e.g. UK statutory 7-year requirement" />
            </div>
            <div className="form-field full-width form-actions">
              <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Save Policy</button>
            </div>
          </form>
        )}

        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Entity Type</th><th>Retention Period</th><th>Basis</th></tr></thead>
            <tbody>
              {policies.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No policies set yet</td></tr>
              ) : policies.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontWeight: 600 }}>{p.entityType}</td>
                  <td>{p.retentionPeriodMonths} months</td>
                  <td>{p.basis || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="page-section">
        <div className="section-header">
          <h2>GDPR Requests</h2>
          <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowAddRequest((s) => !s)}>
            <FiPlus size={14} /> {showAddRequest ? 'Cancel' : 'Log Request'}
          </button>
        </div>

        {showAddRequest && (
          <form onSubmit={handleAddRequest} className="form-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="form-field">
              <label>Subject Type *</label>
              <select value={requestForm.subjectType} onChange={(e) => setRequestForm(p => ({ ...p, subjectType: e.target.value }))}>
                <option value="user">User</option>
                <option value="employee">Employee</option>
                <option value="customer">Customer</option>
                <option value="candidate">Candidate</option>
                <option value="subscriber">Subscriber</option>
              </select>
            </div>
            <div className="form-field">
              <label>Subject ID *</label>
              <input required value={requestForm.subjectId} onChange={(e) => setRequestForm(p => ({ ...p, subjectId: e.target.value }))} placeholder="Their record ID" />
            </div>
            <div className="form-field">
              <label>Request Type *</label>
              <select value={requestForm.requestType} onChange={(e) => setRequestForm(p => ({ ...p, requestType: e.target.value }))}>
                <option value="access">Access</option>
                <option value="export">Export</option>
                <option value="deletion">Deletion</option>
                <option value="correction">Correction</option>
              </select>
            </div>
            <div className="form-field">
              <label>Legal Basis</label>
              <input value={requestForm.legalBasis} onChange={(e) => setRequestForm(p => ({ ...p, legalBasis: e.target.value }))} />
            </div>
            <div className="form-field full-width form-actions">
              <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Log Request</button>
            </div>
          </form>
        )}

        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Subject</th><th>Type</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {gdprRequests.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No requests logged</td></tr>
              ) : gdprRequests.map((r) => (
                <tr key={r.id}>
                  <td>{r.subjectType} #{r.subjectId}</td>
                  <td style={{ textTransform: 'capitalize' }}>{r.requestType}</td>
                  <td>
                    <span className="badge" style={{ background: r.status === 'completed' ? '#10b981' : r.status === 'rejected' ? '#ef4444' : '#f59e0b', color: 'white' }}>
                      {r.status}
                    </span>
                  </td>
                  <td>
                    {r.status === 'pending' && (
                      <div className="actions">
                        <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleProcessRequest(r.id, 'in_progress')}>Start</button>
                        <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleProcessRequest(r.id, 'completed')}>Complete</button>
                        <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleProcessRequest(r.id, 'rejected')}>Reject</button>
                      </div>
                    )}
                    {r.status === 'in_progress' && (
                      <div className="actions">
                        <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleProcessRequest(r.id, 'completed')}>Complete</button>
                        <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleProcessRequest(r.id, 'rejected')}>Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </motion.div>
  );
};

export default Compliance;